import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Key, Eye, EyeOff, Plus, Save, Shield, Lock, AlertTriangle,
  FileText, Building, User, Loader2, Pencil, Trash2
} from 'lucide-react';
import api, { formatErrorMessage } from '../utils/api';
import { toast } from 'sonner';

const ClientCredentials = ({ clientId, clientName, canEdit = false }) => {
  const [credentials, setCredentials] = useState(null);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados de visibilidad para cada campo
  const [visibleFields, setVisibleFields] = useState({
    rut: false,
    clave_sii: false,
    clave_previred: false,
    clave_unica: false
  });
  const [visibleOtras, setVisibleOtras] = useState({});
  
  // Estados para edición
  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState({
    rut: '',
    clave_sii: '',
    clave_previred: '',
    clave_unica: '',
    otras_claves: {},
    notas: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Estado para nueva clave personalizada
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');

  useEffect(() => {
    if (clientId) {
      fetchCredentials();
    }
  }, [clientId]);

  // Auto-ocultar campos después de 30 segundos
  useEffect(() => {
    const timers = [];
    Object.entries(visibleFields).forEach(([field, isVisible]) => {
      if (isVisible) {
        const timer = setTimeout(() => {
          setVisibleFields(prev => ({ ...prev, [field]: false }));
        }, 30000);
        timers.push(timer);
      }
    });
    return () => timers.forEach(t => clearTimeout(t));
  }, [visibleFields]);

  const fetchCredentials = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/clients/${clientId}/credentials`);
      setHasCredentials(response.data.has_credentials);
      setCredentials(response.data.credentials);
      
      if (response.data.credentials) {
        setEditData({
          rut: response.data.credentials.rut || '',
          clave_sii: response.data.credentials.clave_sii || '',
          clave_previred: response.data.credentials.clave_previred || '',
          clave_unica: response.data.credentials.clave_unica || '',
          otras_claves: response.data.credentials.otras_claves || {},
          notas: response.data.credentials.notas || ''
        });
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError('No tienes permiso para ver las credenciales de este cliente.');
      } else {
        setError(formatErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleFieldVisibility = (field) => {
    setVisibleFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const toggleOtraVisibility = (key) => {
    setVisibleOtras(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const maskValue = (value) => {
    if (!value) return '••••••••';
    return '•'.repeat(Math.min(value.length, 12));
  };

  const handleSaveCredentials = async () => {
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/credentials`, {
        client_id: clientId,
        ...editData
      });
      toast.success('Credenciales guardadas correctamente');
      setShowEdit(false);
      fetchCredentials();
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleAddOtraClave = () => {
    if (!newKeyName.trim()) {
      toast.error('Ingrese un nombre para la clave');
      return;
    }
    setEditData(prev => ({
      ...prev,
      otras_claves: {
        ...prev.otras_claves,
        [newKeyName.trim()]: newKeyValue
      }
    }));
    setNewKeyName('');
    setNewKeyValue('');
  };

  const handleRemoveOtraClave = (keyName) => {
    setEditData(prev => {
      const newOtras = { ...prev.otras_claves };
      delete newOtras[keyName];
      return { ...prev, otras_claves: newOtras };
    });
  };

  const renderCredentialField = (label, field, value, icon) => {
    const Icon = icon;
    const isVisible = visibleFields[field];
    
    return (
      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center gap-3 flex-1">
          <div className="p-2 rounded-lg bg-slate-200">
            <Icon className="w-4 h-4 text-slate-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-slate-500 font-medium">{label}</p>
            <p className={`font-mono text-sm ${isVisible ? 'text-slate-900' : 'text-slate-400'}`}>
              {value ? (isVisible ? value : maskValue(value)) : <span className="italic text-slate-400">No registrada</span>}
            </p>
          </div>
        </div>
        {value && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleFieldVisibility(field)}
            className="text-slate-500 hover:text-primary"
            data-testid={`toggle-${field}`}
          >
            {isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="p-6 border-amber-200 bg-amber-50/50">
        <div className="flex items-center justify-center gap-2 text-amber-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Cargando credenciales...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6 border-red-200 bg-red-50/50">
        <div className="flex items-center gap-3 text-red-600">
          <Lock className="w-5 h-5" />
          <div>
            <p className="font-medium">Acceso Restringido</p>
            <p className="text-sm text-red-500">{error}</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50/50 to-white overflow-hidden">
      {/* Header */}
      <div className="p-4 bg-amber-100/50 border-b border-amber-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-200">
            <Key className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <h3 className="font-semibold text-amber-900">Credenciales del Cliente</h3>
            <p className="text-xs text-amber-600">{clientName}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 px-2 py-1 rounded bg-amber-200/50 text-amber-700 text-xs">
            <Shield className="w-3 h-3" />
            Acceso Restringido
          </div>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEdit(true)}
              className="text-amber-700 border-amber-300 hover:bg-amber-100"
              data-testid="edit-credentials-btn"
            >
              {hasCredentials ? <Pencil className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              {hasCredentials ? 'Editar' : 'Agregar'}
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {!hasCredentials ? (
          <div className="text-center py-6">
            <Lock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No hay credenciales registradas para este cliente</p>
            {canEdit && (
              <Button
                variant="outline"
                onClick={() => setShowEdit(true)}
                className="mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Registrar Credenciales
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {/* Campos principales */}
            {renderCredentialField('RUT del Cliente', 'rut', credentials?.rut, User)}
            {renderCredentialField('Clave SII', 'clave_sii', credentials?.clave_sii, Building)}
            {renderCredentialField('Clave Previred', 'clave_previred', credentials?.clave_previred, FileText)}
            {renderCredentialField('Clave Única', 'clave_unica', credentials?.clave_unica, Key)}
            
            {/* Otras claves */}
            {credentials?.otras_claves && Object.keys(credentials.otras_claves).length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-slate-500 font-medium mb-2 uppercase">Otras Claves</p>
                <div className="space-y-2">
                  {Object.entries(credentials.otras_claves).map(([keyName, keyValue]) => (
                    <div key={keyName} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="p-2 rounded-lg bg-slate-200">
                          <Lock className="w-4 h-4 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-slate-500 font-medium">{keyName}</p>
                          <p className={`font-mono text-sm ${visibleOtras[keyName] ? 'text-slate-900' : 'text-slate-400'}`}>
                            {visibleOtras[keyName] ? keyValue : maskValue(keyValue)}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleOtraVisibility(keyName)}
                        className="text-slate-500 hover:text-primary"
                      >
                        {visibleOtras[keyName] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Notas */}
            {credentials?.notas && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-600 font-medium mb-1">Notas</p>
                <p className="text-sm text-blue-800">{credentials.notas}</p>
              </div>
            )}
            
            {/* Metadata */}
            <div className="mt-4 pt-3 border-t border-slate-200 text-xs text-slate-400">
              {credentials?.created_by && (
                <p>Creado por: {credentials.created_by}</p>
              )}
              {credentials?.updated_by && (
                <p>Última actualización: {credentials.updated_by}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de edición */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Key className="w-5 h-5" />
              {hasCredentials ? 'Editar' : 'Registrar'} Credenciales
            </DialogTitle>
            <DialogDescription>
              {clientName} - Estos datos son confidenciales y solo serán visibles para usuarios autorizados.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
              <p className="text-sm text-amber-700">
                Las credenciales solo serán visibles para Socios, Líderes y funcionarios asignados a tareas de este cliente.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>RUT del Cliente</Label>
                <Input
                  value={editData.rut}
                  onChange={(e) => setEditData({ ...editData, rut: e.target.value })}
                  placeholder="12.345.678-9"
                  data-testid="input-rut"
                />
              </div>
              <div className="space-y-2">
                <Label>Clave SII</Label>
                <Input
                  type="password"
                  value={editData.clave_sii}
                  onChange={(e) => setEditData({ ...editData, clave_sii: e.target.value })}
                  placeholder="••••••••"
                  data-testid="input-clave-sii"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Clave Previred</Label>
                <Input
                  type="password"
                  value={editData.clave_previred}
                  onChange={(e) => setEditData({ ...editData, clave_previred: e.target.value })}
                  placeholder="••••••••"
                  data-testid="input-clave-previred"
                />
              </div>
              <div className="space-y-2">
                <Label>Clave Única</Label>
                <Input
                  type="password"
                  value={editData.clave_unica}
                  onChange={(e) => setEditData({ ...editData, clave_unica: e.target.value })}
                  placeholder="••••••••"
                  data-testid="input-clave-unica"
                />
              </div>
            </div>
            
            {/* Otras claves */}
            <div className="space-y-2">
              <Label>Otras Claves (Tesorería, Municipalidades, etc.)</Label>
              <div className="space-y-2">
                {Object.entries(editData.otras_claves).map(([keyName, keyValue]) => (
                  <div key={keyName} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium text-slate-700 w-32 truncate">{keyName}:</span>
                    <Input
                      type="password"
                      value={keyValue}
                      onChange={(e) => setEditData(prev => ({
                        ...prev,
                        otras_claves: { ...prev.otras_claves, [keyName]: e.target.value }
                      }))}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveOtraClave(keyName)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                
                {/* Agregar nueva clave */}
                <div className="flex items-end gap-2 pt-2 border-t border-slate-200">
                  <div className="flex-1">
                    <Label className="text-xs">Nombre de la clave</Label>
                    <Input
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="Ej: Tesorería"
                      className="mt-1"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Valor</Label>
                    <Input
                      type="password"
                      value={newKeyValue}
                      onChange={(e) => setNewKeyValue(e.target.value)}
                      placeholder="••••••••"
                      className="mt-1"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddOtraClave}
                    disabled={!newKeyName.trim()}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Notas */}
            <div className="space-y-2">
              <Label>Notas adicionales</Label>
              <Textarea
                value={editData.notas}
                onChange={(e) => setEditData({ ...editData, notas: e.target.value })}
                placeholder="Información adicional sobre las credenciales..."
                rows={3}
                data-testid="input-notas"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowEdit(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveCredentials}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                disabled={saving}
                data-testid="save-credentials-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Credenciales
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ClientCredentials;
